import i18n from 'i18next'
import ICU from 'i18next-icu'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'

import { env } from './env'
import { LANGUAGES, type UiLanguage } from './types'
import { intlLocale } from './utils'

import enCommon from '@/locales/en/common.json'
import enAuth from '@/locales/en/auth.json'
import enCatalog from '@/locales/en/catalog.json'
import enBook from '@/locales/en/book.json'
import enReviews from '@/locales/en/reviews.json'
import enAdmin from '@/locales/en/admin.json'
import enErrors from '@/locales/en/errors.json'

import ruCommon from '@/locales/ru/common.json'
import ruAuth from '@/locales/ru/auth.json'
import ruCatalog from '@/locales/ru/catalog.json'
import ruBook from '@/locales/ru/book.json'
import ruReviews from '@/locales/ru/reviews.json'
import ruAdmin from '@/locales/ru/admin.json'
import ruErrors from '@/locales/ru/errors.json'

import uzCommon from '@/locales/uz/common.json'
import uzAuth from '@/locales/uz/auth.json'
import uzCatalog from '@/locales/uz/catalog.json'
import uzBook from '@/locales/uz/book.json'
import uzReviews from '@/locales/uz/reviews.json'
import uzAdmin from '@/locales/uz/admin.json'
import uzErrors from '@/locales/uz/errors.json'

export const NAMESPACES = [
  'common',
  'auth',
  'catalog',
  'book',
  'reviews',
  'admin',
  'errors',
] as const

const resources = {
  en: {
    common: enCommon,
    auth: enAuth,
    catalog: enCatalog,
    book: enBook,
    reviews: enReviews,
    admin: enAdmin,
    errors: enErrors,
  },
  ru: {
    common: ruCommon,
    auth: ruAuth,
    catalog: ruCatalog,
    book: ruBook,
    reviews: ruReviews,
    admin: ruAdmin,
    errors: ruErrors,
  },
  uz: {
    common: uzCommon,
    auth: uzAuth,
    catalog: uzCatalog,
    book: uzBook,
    reviews: uzReviews,
    admin: uzAdmin,
    errors: uzErrors,
  },
} as const

export const LANGUAGE_STORAGE_KEY = 'itpu.language'

export function isUiLanguage(value: unknown): value is UiLanguage {
  return typeof value === 'string' && (LANGUAGES as readonly string[]).includes(value)
}

/**
 * Resolution order from FR-7.3: `?lang=` → the signed-in profile (applied later
 * by the auth layer) → localStorage → Accept-Language → en. The detector covers
 * everything except the profile, which `applyProfileLanguage` folds in once the
 * user is known.
 */
void i18n
  .use(ICU)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    ns: NAMESPACES as unknown as string[],
    defaultNS: 'common',
    fallbackLng: env.defaultLanguage,
    supportedLngs: LANGUAGES as unknown as string[],
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    detection: {
      order: ['querystring', 'localStorage', 'navigator'],
      lookupQuerystring: 'lang',
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },
    interpolation: { escapeValue: false },
    returnNull: false,
    // ICU needs the full locale tag to pick the right plural rules (FR-7.7).
    i18nFormat: { localeData: 'en' },
  })

function applyDocumentLanguage(language: string): void {
  document.documentElement.lang = language
  // All three supported languages are left-to-right (§13.1).
  document.documentElement.dir = 'ltr'
}

applyDocumentLanguage(i18n.resolvedLanguage ?? env.defaultLanguage)
i18n.on('languageChanged', applyDocumentLanguage)

/**
 * Set as soon as the visitor picks a language themselves.
 *
 * FR-7.3 puts the signed-in profile above localStorage, but not above a choice
 * made in this session. The profile arrives a round trip after the shell has
 * already rendered, and a reader who reached for the switcher in that moment
 * had their click silently undone a second later.
 */
let chosenInThisSession = false

export async function changeLanguage(
  language: UiLanguage,
  options: { explicit?: boolean } = {},
): Promise<void> {
  if (options.explicit !== false) chosenInThisSession = true
  await i18n.changeLanguage(language)
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
  } catch {
    /* private mode — the choice lasts for this session only */
  }
}

/**
 * Applies the language saved on the user's profile, unless the URL carried an
 * explicit `?lang=`, which always wins (FR-7.3).
 */
export async function applyProfileLanguage(language: UiLanguage): Promise<void> {
  if (chosenInThisSession) return
  const explicit = new URLSearchParams(window.location.search).get('lang')
  if (isUiLanguage(explicit)) return
  if (i18n.resolvedLanguage === language) return
  await changeLanguage(language, { explicit: false })
}

export function currentLanguage(): UiLanguage {
  return isUiLanguage(i18n.resolvedLanguage) ? i18n.resolvedLanguage : env.defaultLanguage
}

export function currentIntlLocale(): string {
  return intlLocale(currentLanguage())
}

export default i18n
